import { useEffect, useRef, useState } from 'react'

import { LAUNCHING_CLASS, frameAt, poseAt } from '../../lib/launchScene'

/**
 * The installed app's launch scene (design: the Android app's, tooti#241):
 * on the brand navy, the parrot flies in, perches on a loading bar until the
 * session check has answered (`ready`), then flaps off while the navy fades
 * into the first screen, and `onDone` fires. The script is lib/launchScene.js;
 * this keeps its clock and draws it.
 *
 * Only the installed app plays it, once per launch: index.html's inline
 * script decides before the first paint (so the page starts navy, never the
 * cream ground) by putting `launching` on <html>, and this takes it off when
 * it is over. A browser tab — the public website — never sees it, and nor does
 * moving around inside the app. Clicks are held while it is up: under the
 * navy the first screen is drawn but not yet seen.
 */
export default function LaunchScene({ ready, onDone }) {
  const [reducedMotion] = useState(prefersReducedMotion)
  const [frame, setFrame] = useState(() => frameAt(0, null, reducedMotion))
  const readyRef = useRef(ready)
  const doneRef = useRef(onDone)

  useEffect(() => {
    readyRef.current = ready
    doneRef.current = onDone
  })

  useEffect(() => {
    let raf = 0
    let start = null
    let readyAt = null
    const tick = (now) => {
      if (start === null) start = now
      const scene = now - start
      if (readyRef.current && readyAt === null) readyAt = scene
      const next = frameAt(scene, readyAt, reducedMotion)
      if (next.phase === 'Done') {
        document.documentElement.classList.remove(LAUNCHING_CLASS)
        doneRef.current?.()
        return
      }
      setFrame(next)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [reducedMotion])

  return <LaunchSceneFrame frame={frame} width={window.innerWidth} height={window.innerHeight} />
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

/** Over everything, the whole viewport; it also catches every click while it is up. */
const OVERLAY = { position: 'fixed', inset: 0, zIndex: 2147483000 }
const NAVY = '#1E4372'
const BAR_TRACK = 'rgba(255,255,255,0.16)'
const BAR_FILL = '#F5C518'
/** The parrot's 288-unit canvas, drawn this big (px). */
const PARROT_BOX = 230
const BAR_WIDTH = 168
const BAR_HEIGHT = 6
/** The bar sits this far below the middle of the screen, so bird and bar together are centred. */
const BAR_BELOW_CENTRE = 44
/** The artwork's placement on the canvas (Parrot below), and where its toes grip the bar. */
const PLACE = { x: 72.6, y: 56.45, scale: 0.85 }
const TOES = { x: PLACE.x + PLACE.scale * 88, y: PLACE.y + PLACE.scale * 177 }

/** One frame of the scene, with no clock of its own: what the render test draws. */
export function LaunchSceneFrame({ frame, width, height }) {
  // Out of sight sideways: half the screen plus the whole bird.
  const pose = poseAt(frame, width / 2 + PARROT_BOX * 0.65)
  const barX = width / 2
  const barY = height / 2 + BAR_BELOW_CENTRE
  const unit = PARROT_BOX / 288
  const left = barX + pose.x - TOES.x * unit
  const top = barY + pose.y - TOES.y * unit
  const barLeft = barX - BAR_WIDTH / 2
  return (
    <div className="launch-scene" aria-hidden="true" style={OVERLAY}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
        <rect width={width} height={height} fill={NAVY} opacity={n(pose.navyAlpha)} />
        {pose.barAlpha > 0 && (
          <g opacity={n(pose.barAlpha)}>
            <rect x={barLeft} y={barY} width={BAR_WIDTH} height={BAR_HEIGHT} rx={BAR_HEIGHT / 2} fill={BAR_TRACK} />
            {pose.barTo > pose.barFrom && (
              <rect
                className="launch-bar-fill"
                x={n(barLeft + BAR_WIDTH * pose.barFrom)}
                y={barY}
                width={n(BAR_WIDTH * (pose.barTo - pose.barFrom))}
                height={BAR_HEIGHT}
                rx={BAR_HEIGHT / 2}
                fill={BAR_FILL}
              />
            )}
          </g>
        )}
        <g
          opacity={n(pose.birdAlpha)}
          transform={`rotate(${n(pose.tilt)} ${n(left + PARROT_BOX / 2)} ${n(top + PARROT_BOX / 2)}) translate(${n(left)} ${n(top)}) scale(${n(unit)})`}
        >
          <Parrot pose={pose} />
        </g>
      </svg>
    </div>
  )
}

/**
 * The full-colour parrot — the Android app's res/drawable/launch_parrot.xml,
 * path for path (Mascot.jsx's drawing, with the feet and a far wing) — on a
 * 288-unit canvas, its joints set to `pose`: the body squashes on its feet,
 * both wings hinge at the shoulder, the lower beak at the head, the eye blinks.
 * At rest the far wing lies exactly under the near one.
 */
function Parrot({ pose }) {
  const wing = 'M40,142 C43,112 70,90 98,97 C122,103 122,130 102,145 C82,160 55,158 40,142 Z'
  return (
    <g transform={`translate(${PLACE.x} ${PLACE.y}) scale(${PLACE.scale})`}>
      {/* Squashing keeps the volume: a shorter body is a little wider. */}
      <g className="joint-body" transform={joint(88, 181, 0, 1 + (1 - pose.squash) * 0.5, pose.squash)}>
        <g className="joint-wing-far" transform={joint(100, 104, pose.wingFar)}>
          <path fill="#C99A10" d={wing} />
        </g>
        <path fill="#F5C518" d="M112,50 A38,38 0 0 1 150,88 L112,88 Z" />
        <g className="joint-jaw" transform={joint(113, 89, pose.jaw)}>
          <path fill="#E0AE13" d="M112,88 L146,88 A26,26 0 0 1 114,104 Z" />
        </g>
        <path
          fill="none"
          stroke="#E0AE13"
          strokeWidth="7"
          strokeLinecap="round"
          d="M76,150 L76,174 M100,150 L100,174 M60,181 C66,170 86,170 92,181 M84,181 C90,170 110,170 116,181"
        />
        <path fill="#2A5C99" d="M66,116 C64,146 48,168 22,180 C16,171 14,165 16,159 C40,147 52,133 58,110 Z" />
        <path fill="#2A5C99" d="M52,55 A31,31 0 0 1 114,55 L114,121 A31,31 0 0 1 52,121 Z" />
        <path fill="#2A5C99" d="M43,124 A41,40 0 1 0 125,124 A41,40 0 1 0 43,124 Z" />
        <g className="joint-wing" transform={joint(106, 110, pose.wing)}>
          <path fill="#E0AE13" d={wing} />
          <path fill="#F5C518" d="M45,138 C50,113 72,95 96,102 C116,108 116,127 99,141 C80,155 59,152 45,138 Z" />
          <path fill="#E0AE13" fillOpacity="0.4" d="M64,146 C84,141 96,129 100,109 C108,129 98,149 76,154 Z" />
        </g>
        <g className="joint-eye" transform={joint(94, 64, 0, 1, pose.eye)}>
          <path fill="#FFFFFF" d="M73,64 A21,21 0 1 0 115,64 A21,21 0 1 0 73,64 Z" />
          <path fill="#1B3A66" d="M79,64 A15,15 0 1 0 109,64 A15,15 0 1 0 79,64 Z" />
          <path fill="#FFFFFF" d="M95.5,57 A4.5,4.5 0 1 0 104.5,57 A4.5,4.5 0 1 0 95.5,57 Z" />
        </g>
      </g>
    </g>
  )
}

/** A VectorDrawable group's transform about its pivot: scale, then rotate. */
function joint(px, py, rotation = 0, sx = 1, sy = 1) {
  return `translate(${px} ${py}) rotate(${n(rotation)}) scale(${n(sx)} ${n(sy)}) translate(${-px} ${-py})`
}

/** Short numbers in the markup. */
function n(x) {
  return Math.round(x * 1000) / 1000
}
